/**
 * XMR swap capability detection.
 *
 * Monero is being added to cross-chain liquidity networks (THORChain's
 * FROST-based XMR client, with Maya likely to follow). Until a pool is
 * actually live we must NOT surface XMR as a swap target — doing so would
 * produce dead UI and failed quotes. This module probes the THORChain and
 * Maya pool endpoints for a live `XMR.XMR` pool and caches the answer, so
 * the Exchange can gate the XMR asset on real on-chain liquidity rather
 * than a hardcoded flag.
 *
 * Fails closed: any network/parse error → treated as "not live".
 */

import { config } from '../../../../config/environment.js';
import { createLogger } from '../../../../utils/logger.js';
import { XMR_IDENTIFIER } from './moneroAddress';

const log = createLogger('XmrSwapCapability');

export const XMR_SWAP_PROVIDER = {
	THORCHAIN: 'THORCHAIN',
	MAYACHAIN: 'MAYACHAIN',
};

const NOT_LIVE = Object.freeze({ live: false, provider: null });

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const PROBE_TIMEOUT_MS = 5000;

// Module-level cache shared across windows. `inflight` dedupes concurrent
// probes (e.g. token load + UI gate firing at once).
const cacheState = {
	status: NOT_LIVE,
	checkedAt: 0,
	inflight: null,
};

const xmrConfig = () => config?.xmr || {};

const thornodeUrl = () =>
	(xmrConfig().thornodeUrl || 'https://gateway.liquify.com/chain/thorchain_api').replace(/\/+$/, '');

const mayanodeUrl = () =>
	(xmrConfig().mayanodeUrl || 'https://mayanode.mayachain.info').replace(/\/+$/, '');

/**
 * GET a single pool record. Returns the parsed pool object or null.
 * @param {string} url
 * @returns {Promise<object|null>}
 */
const fetchPool = async (url) => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
	try {
		const res = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
		if (!res.ok) return null; // 404 → pool not registered yet
		const data = await res.json();
		return data && typeof data === 'object' ? data : null;
	} catch (err) {
		log.debug(`pool probe failed for ${url}: ${err?.message || err}`);
		return null;
	} finally {
		clearTimeout(timer);
	}
};

// A pool is tradeable only when staged/available, not when suspended.
const isPoolTradeable = (pool) =>
	!!pool && typeof pool.status === 'string' && pool.status.toLowerCase() === 'available';

const probeThorchain = async () => {
	const pool = await fetchPool(`${thornodeUrl()}/thorchain/pool/${XMR_IDENTIFIER}`);
	return isPoolTradeable(pool);
};

const probeMaya = async () => {
	const pool = await fetchPool(`${mayanodeUrl()}/mayachain/pool/${XMR_IDENTIFIER}`);
	return isPoolTradeable(pool);
};

const runDetection = async () => {
	// Probe both venues in parallel so a hanging endpoint can't double the
	// wait (worst case is one PROBE_TIMEOUT_MS, not two). THORChain is the
	// primary/expected XMR venue, so it wins when both report live.
	const [thorLive, mayaLive] = await Promise.all([
		probeThorchain().catch((err) => {
			log.debug(`thorchain probe error: ${err?.message || err}`);
			return false;
		}),
		probeMaya().catch((err) => {
			log.debug(`maya probe error: ${err?.message || err}`);
			return false;
		}),
	]);

	if (thorLive) return { live: true, provider: XMR_SWAP_PROVIDER.THORCHAIN };
	if (mayaLive) return { live: true, provider: XMR_SWAP_PROVIDER.MAYACHAIN };
	return NOT_LIVE;
};

/**
 * Resolve whether XMR swaps are currently live, with caching.
 * @param {{force?: boolean}} [opts]
 * @returns {Promise<{live: boolean, provider: string|null}>}
 */
export const detectLiveXmrSwap = async ({ force = false } = {}) => {
	// Master kill-switch: if XMR swaps are disabled in config, never probe.
	if (xmrConfig().enabled === false) {
		cacheState.status = NOT_LIVE;
		cacheState.checkedAt = Date.now();
		return NOT_LIVE;
	}

	const fresh = Date.now() - cacheState.checkedAt < CACHE_TTL_MS;
	if (!force && fresh && cacheState.checkedAt > 0) {
		return cacheState.status;
	}

	if (cacheState.inflight) return cacheState.inflight;

	cacheState.inflight = (async () => {
		try {
			const status = await runDetection();
			cacheState.status = status;
			cacheState.checkedAt = Date.now();
			if (status.live) {
				log.info(`XMR swaps live via ${status.provider}`);
			}
			return status;
		} finally {
			cacheState.inflight = null;
		}
	})();

	return cacheState.inflight;
};

/**
 * Synchronous read of the last known status (no network). Useful for render
 * gating where awaiting is impractical; pair with detectLiveXmrSwap() to warm
 * the cache.
 * @returns {{live: boolean, provider: string|null}}
 */
export const getCachedXmrSwapStatus = () => cacheState.status;

/** Test-only: reset the module cache. */
export const __resetXmrSwapCapabilityCache = () => {
	cacheState.status = NOT_LIVE;
	cacheState.checkedAt = 0;
	cacheState.inflight = null;
};
